import { BaseService } from './base';
import { ApiClient } from '../lib/client';
import { ApiException } from '../lib/exception';
import { ApiRequest } from '../lib/request';
import { ApiListResponse, type ApiResponse } from '../lib/response';
import { CustomerMessage, CustomerMessageSender } from '../models/customerMessage';
import { Customer } from '../models/customer';

type CustomerMessageListResponse = ApiListResponse<CustomerMessage> & {
  customer?: Customer | null;
};

export class CustomerMessageService extends BaseService<CustomerMessage> {
  override model = CustomerMessage;

  override modelName = 'customerMessage';
  override modelLabel = 'Customer Messages';

  async counts(): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/counts`,
    });

    return await this.request(request);
  }

  async customer(
    customerId: string | number,
    { markAsRead = true, page = 1, limit = 10 }: { markAsRead?: boolean; page?: number; limit?: number } = {}
  ): Promise<CustomerMessageListResponse> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/customer`,
      query: {
        page: page.toString(),
        limit: limit.toString(),
        markAsRead: markAsRead ? '1' : '0',
      },
      data: { customer: customerId },
    });

    const response = await ApiClient.request(request);

    if (!response.success) {
      throw new ApiException(response);
    }

    const list = new ApiListResponse<CustomerMessage>(response, CustomerMessage) as CustomerMessageListResponse;
    list.customer = response.customer ? new Customer(response.customer) : null;

    return list;
  }

  async send({
    customerId,
    subject,
    body,
    attachments,
    sender = CustomerMessageSender.location,
    emailSend,
    smsSend,
  }: {
    customerId: string;
    subject: string;
    body: string;
    attachments?: any[];
    sender?: CustomerMessageSender;
    emailSend?: boolean;
    smsSend?: boolean;
  }, uploads: Record<string, File> = {}): Promise<ApiResponse<CustomerMessage>> {
    const formData = new FormData();

    const data = {
      idCustomer: customerId,
      subject,
      body,
      attachments,
      sender,
      emailSend,
      smsSend,
    };

    formData.append('_request', JSON.stringify(data));
    formData.append('_method', 'POST');

    for (const key in uploads) {
      formData.append(key, uploads[key]);
    }

    const request = new ApiRequest({
      method: 'POST',
      url: this.modelBaseUrl,
      data: formData,
    });

    return await this.requestItem<CustomerMessage>(request, CustomerMessage);
  }
}
